import React, { useContext, useEffect, useMemo, useState } from "react";

import backend from "../../../backend";
import AuthContext from "../../../AuthContext";
import ExpandableTable from "../../../components/design-system/ExpandableTable";
import StatusBadge from "../../../components/design-system/StatusBadge";
import ConfirmModal from "../../../components/design-system/ConfirmModal";
import { timeAgo, formatDate } from "../../../utils";

import RoleSelector, { ROLE_INFO } from "./RoleSelector";
import NewUserModal from "./NewUserModal";
import TempPasswordModal from "./TempPasswordModal";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RoleBadges = ({ roles = [] }) => {
  if (!roles.length) {
    return <span style={{ color: "var(--bini-text-disabled)", fontSize: 10 }}>sin roles</span>;
  }
  return (
    <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
      {roles.map(r => {
        const info = ROLE_INFO[r];
        const label = info?.label || r;
        const color = info?.color || "#888";
        return (
          <span
            key={r}
            className="bini-badge"
            style={{
              background: "transparent",
              border: `1px solid ${color}`,
              color,
            }}
          >
            {label}
          </span>
        );
      })}
    </span>
  );
};

const formatAbsolute = d => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("es-CL");
  } catch {
    return "—";
  }
};

const UsersPanel = () => {
  const authContext = useContext(AuthContext);
  const currentUserId = authContext?.user?._id;
  const puedeAdministrar = authContext?.can?.("administrar_usuarios") || false;

  const [users, setUsers]               = useState([]);
  const [roles, setRoles]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [loadErr, setLoadErr]           = useState(null);

  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [roleFilter, setRoleFilter]     = useState(null);

  const [newOpen, setNewOpen]           = useState(false);
  const [tempPwd, setTempPwd]           = useState(null);

  const [archiveTarget, setArchiveTarget]   = useState(null);
  const [reactivateTarget, setReactivateTarget] = useState(null);
  const [resetTarget, setResetTarget]   = useState(null);

  const reload = async () => {
    try {
      const [u, r] = await Promise.all([
        backend.get("user/"),
        backend.get("user/roles"),
      ]);
      setUsers(Array.isArray(u.data) ? u.data : []);
      const list = Array.isArray(r.data?.roles) ? r.data.roles
                : Array.isArray(r.data) ? r.data
                : [];
      setRoles(list);
      setLoadErr(null);
    } catch (e) {
      setLoadErr(e?.response?.data || e.message);
    }
  };

  useEffect(() => {
    if (!puedeAdministrar) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      await reload();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [puedeAdministrar]);

  const filtered = useMemo(() => {
    return users.filter(u => {
      if (statusFilter === "active"   && u.status !== "active")   return false;
      if (statusFilter === "archived" && u.status !== "archived") return false;
      if (roleFilter && !u.roles?.includes(roleFilter)) return false;
      if (search) {
        const s = search.toLowerCase();
        const fullName = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
        return (u.email || "").toLowerCase().includes(s) || fullName.includes(s);
      }
      return true;
    });
  }, [users, statusFilter, roleFilter, search]);

  const handleSave = async (id, draft) => {
    if (!draft) return;
    if (!draft.email || !EMAIL_RX.test(draft.email)) {
      throw new Error("Email inválido");
    }
    if (!draft.firstName?.trim() || !draft.lastName?.trim()) {
      throw new Error("Nombre y apellido requeridos");
    }
    if (!draft.roles || draft.roles.length === 0) {
      throw new Error("Seleccioná al menos un rol");
    }
    await backend.patch("user/", {
      _id: id,
      email: draft.email.trim(),
      firstName: draft.firstName.trim(),
      lastName: draft.lastName.trim(),
      roles: draft.roles,
    });
    await reload();
  };

  const onArchive = async () => {
    if (!archiveTarget) return;
    await backend.patch("user/archive", { _id: archiveTarget._id });
    setArchiveTarget(null);
    await reload();
  };

  const onReactivate = async () => {
    if (!reactivateTarget) return;
    await backend.patch("user/", { _id: reactivateTarget._id, status: "active" });
    setReactivateTarget(null);
    await reload();
  };

  const onReset = async () => {
    if (!resetTarget) return;
    const r = await backend.post(`user/${resetTarget._id}/reset-password`);
    const pwd = r.data?.temporaryPassword;
    setResetTarget(null);
    if (pwd) {
      setTempPwd({ email: r.data?.email || resetTarget.email, password: pwd });
    } else if (r.data?.mode === "email") {
      // email mode: nada que mostrar, la lista se refresca
    }
    await reload();
  };

  const handleCreated = data => {
    setNewOpen(false);
    if (data?.mode === "manual" && data?.temporaryPassword) {
      setTempPwd({ email: data.user?.email, password: data.temporaryPassword });
    }
    reload();
  };

  const columns = [
    { key: "email", label: "EMAIL", width: "28%",
      render: v => <span style={{ color: "var(--bini-text-primary)" }}>{v}</span> },
    { key: "name",  label: "NOMBRE", width: "22%",
      render: (_, row) => `${row.firstName || ""} ${row.lastName || ""}`.trim() || "—" },
    { key: "roles", label: "ROLES", width: "20%",
      render: v => <RoleBadges roles={v} /> },
    { key: "status", label: "ESTADO", width: "12%",
      render: v => v === "archived"
        ? <StatusBadge status="archived" label="● Archivado" />
        : <StatusBadge status="active"   label="● Activo"    /> },
    { key: "lastLogin", label: "ÚLTIMO LOGIN", width: "18%",
      render: v => (
        <span title={v ? formatAbsolute(v) : "Nunca"} style={{ color: "var(--bini-text-secondary)" }}>
          {timeAgo(v)}
        </span>
      ) },
  ];

  const renderDetail = (row, helpers) => {
    const isMe = row._id === currentUserId;
    if (helpers.isEditing) {
      return (
        <div>
          <div className="bini-section-title">Editar usuario</div>
          <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="bini-field">
              <label>Email</label>
              <input
                className="bini-input"
                value={row.email || ""}
                onChange={e => helpers.updateDraft({ email: e.target.value })}
              />
            </div>
            <div className="bini-field">
              <label>Estado</label>
              <div className="val">
                {row.status === "archived" ? "Archivado" : "Activo"}
                <span style={{ marginLeft: 8, color: "var(--bini-text-disabled)", fontSize: 10 }}>
                  (cambiá vía botón Archivar/Reactivar)
                </span>
              </div>
            </div>
            <div className="bini-field">
              <label>Nombre</label>
              <input
                className="bini-input"
                value={row.firstName || ""}
                onChange={e => helpers.updateDraft({ firstName: e.target.value })}
              />
            </div>
            <div className="bini-field">
              <label>Apellido</label>
              <input
                className="bini-input"
                value={row.lastName || ""}
                onChange={e => helpers.updateDraft({ lastName: e.target.value })}
              />
            </div>
          </div>

          <div className="bini-field">
            <label>Roles</label>
            <RoleSelector
              value={row.roles || []}
              onChange={next => helpers.updateDraft({ roles: next })}
              disabled={helpers.isSaving}
            />
          </div>

          <div className="bini-action-bar">
            <div className="bini-action-bar-l" />
            <div className="bini-action-bar-r">
              <button className="bini-btn bini-btn-cancel" onClick={helpers.cancelEdit} disabled={helpers.isSaving}>
                Cancelar
              </button>
              <button className="bini-btn bini-btn-save" onClick={helpers.saveEdit} disabled={helpers.isSaving}>
                {helpers.isSaving ? "Guardando…" : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className="grid" style={{ gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          <div className="bini-field">
            <label>Email</label>
            <div className="val">{row.email}</div>
          </div>
          <div className="bini-field">
            <label>Nombre completo</label>
            <div className="val">{`${row.firstName || ""} ${row.lastName || ""}`.trim() || "—"}</div>
          </div>
          <div className="bini-field">
            <label>Estado</label>
            <div className="val">
              {row.status === "archived"
                ? <StatusBadge status="archived" label="● Archivado" />
                : <StatusBadge status="active"   label="● Activo"    />}
            </div>
          </div>

          <div className="bini-field">
            <label>Roles</label>
            <div className="val"><RoleBadges roles={row.roles} /></div>
          </div>
          <div className="bini-field">
            <label>Creado</label>
            <div className="val">{formatDate(row.createdAt)}</div>
          </div>
          <div className="bini-field">
            <label>Último login</label>
            <div className="val" title={row.lastLogin ? formatAbsolute(row.lastLogin) : "Nunca"}>
              {timeAgo(row.lastLogin)}
            </div>
          </div>

          <div className="bini-field">
            <label>Password temporal</label>
            <div className="val">
              {row.mustResetPassword
                ? <span className="bini-badge b-temp">● Pendiente de cambio</span>
                : <span style={{ color: "var(--bini-text-muted)", fontSize: 11 }}>—</span>}
            </div>
          </div>
          <div className="bini-field">
            <label>ID</label>
            <div className="val mono" style={{ fontSize: 10, color: "var(--bini-text-disabled)" }}>
              {row._id}
            </div>
          </div>
          <div />
        </div>

        <div className="bini-action-bar">
          <div className="bini-action-bar-l">
            {isMe && (
              <span style={{ color: "var(--bini-text-muted)", fontSize: 11 }}>
                Es tu cuenta — algunas acciones están deshabilitadas
              </span>
            )}
          </div>
          <div className="bini-action-bar-r">
            <button className="bini-btn bini-btn-edit" onClick={helpers.startEdit}>
              ✎ Editar
            </button>
            <button
              className="bini-btn bini-btn-icon"
              onClick={() => setResetTarget(row)}
              disabled={isMe}
              title={isMe ? "No podés resetear tu propia password desde acá" : ""}
            >
              🔑 Resetear password
            </button>
            {row.status === "active"
              ? (
                <button
                  className="bini-btn bini-btn-danger"
                  onClick={() => setArchiveTarget(row)}
                  disabled={isMe}
                  title={isMe ? "No podés archivar tu propia cuenta" : ""}
                >
                  × Archivar
                </button>
              )
              : (
                <button
                  className="bini-btn bini-btn-success"
                  onClick={() => setReactivateTarget(row)}
                >
                  ↺ Reactivar
                </button>
              )
            }
          </div>
        </div>
      </div>
    );
  };

  if (!puedeAdministrar) {
    return (
      <div className="bini-warning">
        <div className="t">⚠ Acceso restringido</div>
        <div className="d">La gestión de usuarios solo está disponible para usuarios con rol <b>admin</b>.</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header: search + nuevo */}
      <div style={{
        display: "flex", gap: 10, alignItems: "center",
        marginBottom: 14, flexWrap: "wrap",
      }}>
        <div style={{ position: "relative", flex: "1 1 280px", maxWidth: 420 }}>
          <input
            className="bini-input"
            placeholder="🔍  Buscar por email o nombre…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button
          className="bini-btn bini-btn-add"
          onClick={() => setNewOpen(true)}
        >
          + Nuevo usuario
        </button>
      </div>

      {/* Filtros chips — chips-scroll: carrusel horizontal en teléfono */}
      <div className="bini-chips-group chips-scroll" style={{ marginBottom: 14 }}>
        <button className={`bini-chip${statusFilter === "all" ? " active" : ""}`}
          onClick={() => setStatusFilter("all")}>Todos</button>
        <button className={`bini-chip${statusFilter === "active" ? " active" : ""}`}
          onClick={() => setStatusFilter("active")}>Activos</button>
        <button className={`bini-chip${statusFilter === "archived" ? " active" : ""}`}
          onClick={() => setStatusFilter("archived")}>Archivados</button>

        <span className="bini-chip-divider" />

        <button className={`bini-chip${roleFilter === null ? " active" : ""}`}
          onClick={() => setRoleFilter(null)}>Todos los roles</button>
        {roles.map(r => (
          <button
            key={r}
            className={`bini-chip${roleFilter === r ? " active" : ""}`}
            onClick={() => setRoleFilter(r)}
          >
            {ROLE_INFO[r]?.label || r}
          </button>
        ))}
      </div>

      {/* Tabla */}
      {loading ? (
        <div style={{ color: "var(--bini-text-muted)", fontSize: 12 }}>Cargando usuarios…</div>
      ) : loadErr ? (
        <div className="bini-warning">
          <div className="t">⚠ Error al cargar usuarios</div>
          <div className="d">{String(loadErr)}</div>
        </div>
      ) : (
        <ExpandableTable
          rows={filtered}
          columns={columns}
          renderDetail={renderDetail}
          onSave={handleSave}
          emptyText={users.length === 0 ? "Sin usuarios" : "Sin resultados con esos filtros"}
        />
      )}

      {/* Modales */}
      <NewUserModal
        open={newOpen}
        onCancel={() => setNewOpen(false)}
        onCreated={handleCreated}
      />

      <TempPasswordModal
        open={!!tempPwd}
        email={tempPwd?.email}
        password={tempPwd?.password}
        onClose={() => setTempPwd(null)}
      />

      <ConfirmModal
        open={!!archiveTarget}
        title="Archivar usuario"
        description={`¿Archivar a ${archiveTarget?.email}? No podrá iniciar sesión hasta que sea reactivado.`}
        warning={{
          title: "Sesiones activas siguen vivas",
          description: "Si el usuario está logueado en otra ventana, sus tokens vigentes seguirán funcionando hasta expirar (revocación inmediata se implementa en F2.3).",
        }}
        confirmLabel="Archivar"
        confirmVariant="danger"
        onConfirm={onArchive}
        onCancel={() => setArchiveTarget(null)}
      />

      <ConfirmModal
        open={!!reactivateTarget}
        title="Reactivar usuario"
        description={`¿Reactivar a ${reactivateTarget?.email}? Va a poder iniciar sesión nuevamente.`}
        confirmLabel="Reactivar"
        confirmVariant="success"
        onConfirm={onReactivate}
        onCancel={() => setReactivateTarget(null)}
      />

      <ConfirmModal
        open={!!resetTarget}
        title="Resetear password"
        description={`Se va a generar una password temporal nueva para ${resetTarget?.email}. La password actual deja de funcionar inmediatamente.`}
        confirmLabel="Resetear"
        confirmVariant="danger"
        onConfirm={onReset}
        onCancel={() => setResetTarget(null)}
      />
    </div>
  );
};

export default UsersPanel;
